/**
 * THE lifecycle ACTIONS-MENU items — Approve, Reject, Mark Ordered, Approve on
 * behalf, Skip remaining approvals — as DESCRIPTORS, shared by
 * `pages/DepartmentOrders.js` and `pages/MyOrders.js`.
 *
 * 🔻 This line said "THE three … — Approve, Reject, Mark Ordered" until
 * 2026-09-13 and had been false since `approveOnBehalf` landed on 2026-09-11.
 * **Count the frozen object below, never this sentence** — and note that
 * MyOrders takes only some of them, which is the whole reason the ITEM LIST is
 * not shared (see below).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS SHARED, AND WHAT IS DELIBERATELY NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Shared: `key`, `category`, `label`, `tone`, and the testid SLUG — everything
 * that decides how the item READS and where it SITS. Two pages offering an
 * action called "Approve" in one colour here and another there is exactly the
 * drift the Code Unification Principle exists to stop, and it is invisible in a
 * diff because each page's copy looks right on its own.
 *
 * 🔴 NOT shared, and this is a constraint rather than an omission:
 *   - the GATE. `client/src/pages/CLAUDE.md` — *"`components/OrderActionsMenu.js`
 *     RESOLVES NOTHING … The PAGE builds the array, with every gate written
 *     verbatim beside its own item, in the place that gate already lived."*
 *     This module never sees an order, a capability or a status, so it cannot
 *     grant anybody anything. Pass it a finished `onClick` and a finished
 *     `disabled`.
 *   - the ITEM LIST. Same file: *"The two pages' ITEM LISTS are deliberately NOT
 *     shared — do not 'unify' them."* That rule governs WHICH items a page
 *     offers; this module governs what ONE item looks like once a page has
 *     decided to offer it. Sharing a descriptor is not unifying a list, and a
 *     future page taking two of these three must stay possible.
 *   - the ENDPOINT. See the block below — the two pages genuinely differ, and
 *     they have to.
 *   - the TITLE, which is `title`-overridable per caller for the same reason.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🚨 THE TWO PAGES CALL DIFFERENT ENDPOINTS FOR APPROVE AND REJECT, AND THAT IS
 *    FORCED — DO NOT "UNIFY" THEM EITHER (2026-09-11)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `DepartmentOrders.js` posts to `/api/approvals/orders/:id/approve` and
 * `…/reject` — it is advancing a real approval chain, one step at a time.
 *
 * `MyOrders.js` CANNOT. `server/routes/approvals.js` answers
 * `404 'No approval workflow for this order'` when the order has no
 * `order_approvals` row, and a My Space order raised since 2026-09-11 has none
 * at all: `POST /api/orders` deliberately builds no chain and sends no
 * notification when the landing department is personal (owner ruling —
 * a personal space has nobody to approve to). So My Space drives all three
 * transitions through `PATCH /api/orders/:id/status`, which is the SAME route
 * the status-override footer already uses on that page, and which resolves any
 * chain a LEGACY My Space order still carries.
 *
 * 🔑 That is why this module stops at the descriptor. A shared `onClick` would
 * have to know which of two server contracts it was in, i.e. it would have to
 * re-derive the thing the page already knows.
 *
 * 🪤 NARROW SEMANTICS: neither page's ITEM sends `reset_side_state`. The
 * status-override FOOTER does, always — so on My Space the footer and the item
 * reach the same route with deliberately different bodies. It is not an
 * oversight and it must not be "made consistent": `reset_side_state` with a
 * target of `ordered` runs
 * `UPDATE order_items SET is_received = 0, received_date = NULL, received_by = NULL`,
 * so a `Mark Ordered` carrying it would silently wipe every received tick on an
 * order that is already part-delivered. DepartmentOrders' own `Mark Ordered`
 * has never sent it; these items mirror that.
 */

/**
 * The descriptors. `testIdSlug` is separate from `key` because the shipped
 * testids are `…-menu-mark-ordered-<id>` while the item's key is `ordered`;
 * both are load-bearing (the key is read by tests and by the menu's own
 * ordering, the testid is pinned by `requiredActions.manifest.test.js`).
 */
export const ORDER_LIFECYCLE_MENU_DESCRIPTORS = Object.freeze({
  approve: Object.freeze({
    key: 'approve',
    // 🔀 2026-09-13 — was `'approval'`. Pete approved merging the `Approval`
    // and `Status` groups into ONE, `Next step`, first in the panel (see
    // `client/src/pages/DepartmentOrders.js`'s categories block for the
    // ruling in full). Renamed HERE rather than at each call site so
    // `MyOrders.js`'s FLAT menu — which never reads `category` at all — is
    // unaffected, while both categorised surfaces (Dept Orders, Approvals)
    // move together.
    category: 'next',
    label: 'Approve',
    tone: 'approve',
    testIdSlug: 'approve',
    title: 'Approve this order and advance it to the next approval step',
  }),
  reject: Object.freeze({
    key: 'reject',
    category: 'next', // 🔀 2026-09-13 — was `'approval'`; see `approve` above.
    label: 'Reject',
    tone: 'reject',
    testIdSlug: 'reject',
    title: 'Send back to the submitter for correction — they can edit and resubmit',
  }),
  ordered: Object.freeze({
    key: 'ordered',
    category: 'next', // 🔀 2026-09-13 — was `'status'`; see `approve` above.
    label: 'Mark Ordered',
    tone: 'progress',
    testIdSlug: 'mark-ordered',
    title: 'Confirm this order has been placed with the supplier',
  }),
  // 🚨 APPROVE ON BEHALF (2026-09-11, owner ruling A) — THE LABEL IS THE
  //    WHOLE POINT, so this is a SEPARATE descriptor and never `approve` with a
  //    different string. Until this ruling a manager saw a plain `Approve` on
  //    EVERY submitted order in the department, because the list forced
  //    `can_action` true on all of them; nothing on screen distinguished *your
  //    step* from *somebody else's*. Jake Dimmer pressed it twice in two minutes
  //    on two surfaces (incident, 2026-08-18) and again on 2026-09-11, and both
  //    times he was not doing anything wrong — the app simply never told him he
  //    was acting for someone else.
  //
  // 🔑 `label` IS A FUNCTION because it NAMES A PERSON. The caller passes
  //    `labelArgs: { approverName }`. A static string could not say whose step
  //    it is, and that is the only information the control adds over the plain
  //    `Approve` it sits beside. The trailing ellipsis is this app's convention
  //    for *an item that opens a dialog rather than acting*, matching `Print…`
  //    and `Share…` in the same menu.
  //
  // 🔴 SAME `category` AS `approve`, deliberately — it is the same verb, and
  //    splitting it into its own section would imply a second kind of approval.
  //    There is NO `rejectOnBehalf` twin: it was not requested, a rejection sends
  //    an order back to its requester with a reason attributed to the rejecter,
  //    and inventing an on-behalf form of that is a product decision nobody has
  //    taken. Reject stays own-step only.
  approveOnBehalf: Object.freeze({
    key: 'approve-on-behalf',
    category: 'next', // 🔀 2026-09-13 — was `'approval'`; see `approve` above.
    label: ({ approverName } = {}) => `Approve on behalf of ${approverName || 'the named approver'}…`,
    tone: 'approve',
    testIdSlug: 'approve-on-behalf',
    title: 'Approve a step that is named for someone else — you will be asked to confirm first',
  }),
  // 🚨 SKIP REMAINING APPROVALS (2026-09-13, owner ruling) — the approval
  //    OVERRIDE, rebuilt as a menu item. Pete, 2026-08-19: forcing an order
  //    through must *"make the jump demand a reason"*; 2026-09-13: *"the
  //    override should require a reason"* and yes to the menu item. Until now
  //    the control existed ONLY on the full order page `/orders/:id`
  //    (`components/ApprovalStatus.js`), by the 2026-08-10 ruling *"keep the
  //    override button on the full order page for now"* — and that page is to
  //    be retired, which is what unblocked this.
  //
  // 🔑 **THE LABEL SAYS WHAT HAPPENS, NOT WHAT THE ROUTE IS CALLED.** "Override"
  //    is the server's word for it and means nothing to the person pressing it;
  //    the act is *skip every remaining approval step and approve this now*. The
  //    trailing ellipsis is this app's convention for *an item that opens a
  //    dialog rather than acting*, matching `Approve on behalf of …`, `Print…`
  //    and `Share…` in the same menu — and here it is load-bearing, because the
  //    dialog is where the mandatory reason is typed.
  //
  // 🔴 **`tone: 'progress'` (amber), NOT `'reject'` — and the brief that asked
  //    for this said "danger-ish", so the departure is recorded.**
  //    `OrderActionsMenu`'s tone vocabulary is
  //    neutral | approve | reject | progress | deliver | nav; there is no
  //    `danger`, and `reject` is RED and already means *the other decision on
  //    this very row* — Reject sits two items above. Painting Skip red would
  //    make the two most consequential approval items look identical at a
  //    glance, which is the class of mistake the 2026-09-11 approve-on-behalf
  //    ruling exists to stop. Amber is the caution tone and is what both
  //    EXISTING Override controls on Department Orders already use
  //    (`bg-amber-500`). **The destructive framing lives in the DIALOG**, which
  //    the page opens with `variant="danger"`. A new `danger` tone would have
  //    meant editing the shared TONE map for one item; not done.
  //
  // 🪤 NO `MyOrders.js` TWIN, and that is a gate decision the page makes, not
  //    this file: My Space is the OWNERSHIP arm (2026-09-11 ruling) and an
  //    owner skipping their own approval chain is the control removing itself.
  override: Object.freeze({
    key: 'override',
    category: 'next',
    label: 'Skip remaining approvals…',
    tone: 'progress',
    testIdSlug: 'override',
    title: 'Skip every remaining approval step and approve this order now — you will be asked for a reason, which is recorded against the order',
  }),
});

/**
 * Build ONE finished `OrderActionsMenu` item from a descriptor.
 *
 * @param {'approve'|'reject'|'ordered'|'approveOnBehalf'|'override'} name
 * @param {object} opts
 * @param {string} opts.testIdPrefix  the page's prefix — `'dept'` or `'my'`.
 * @param {number|string} opts.orderId
 * @param {Function} opts.onClick     already bound by the caller.
 * @param {boolean} [opts.disabled]   the caller's submit-guard mirror.
 * @param {string}  [opts.title]      override when the default would be FALSE
 *                                    on this surface (My Space has no "next
 *                                    approval step" to advance to).
 * @param {object}  [opts.labelArgs]  for a descriptor whose `label` is a
 *                                    function — `approveOnBehalf` takes
 *                                    `{ approverName }`.
 * @returns {object} the item — never a gate, never a list.
 */
export function orderLifecycleMenuItem(name, { testIdPrefix, orderId, onClick, disabled, title, labelArgs } = {}) {
  const d = ORDER_LIFECYCLE_MENU_DESCRIPTORS[name];
  // Fail LOUD, not silently: an unknown name would otherwise render an item
  // with no label and no testid, which reads as a styling bug rather than a
  // typo and is exactly the "a control offered with no handler behind it"
  // shape this repo already ratchets.
  if (!d) throw new Error(`orderLifecycleMenuItem: unknown item "${name}"`);
  return {
    key: d.key,
    category: d.category,
    // 🪤 A descriptor's `label` may be a FUNCTION of `labelArgs` (see
    // `approveOnBehalf`, which names a person). Resolved HERE, once, so no
    // caller ever has to know which kind it got — a page that had to branch on
    // `typeof d.label` would be re-deriving the descriptor's own shape.
    label: typeof d.label === 'function' ? d.label(labelArgs || {}) : d.label,
    tone: d.tone,
    disabled,
    testId: `${testIdPrefix}-menu-${d.testIdSlug}-${orderId}`,
    title: title || d.title,
    onClick,
  };
}

export default orderLifecycleMenuItem;
